// Тексты окна «Сменить владельца» (038: FR-002, FR-005 – FR-018; contracts/ui.md 038; LEAD-01 –
// LEAD-08; 040, T037; FR-021: роль «Ведущий» на экране — «Владелец»). Одно пояснение «Ведущий протокола — ответственный за его ведение» (слова владельца
// 02.11.2026) берут пункт меню, подзаголовок окна, подсказка колонки «Ведущий» в «Документах»
// и подсказка поля «Ведущий» в «Свойствах». Слово «ответственный» без уточнения для ведущего
// не используется: в продукте это исполнитель пункта. Последствие для прежнего ведущего —
// leadAfterAccessText из контрактов (research Р6).
import { leadAfterAccessText } from '@kp/contracts';
import type { LeadAfterAccess } from '@kp/contracts';

/** Точка в конце предложения — если ФИО «Морозов О. С.» уже не кончается ею. */
function sentenceEnd(text: string): string {
  return text.endsWith('.') ? text : `${text}.`;
}

/** Что такое владелец (FR-002). */
export const LEAD_MEANING = 'Владелец протокола — ответственный за его ведение';

/** Пункт меню «Документов» и «Действий протокола», кнопка окна «Доступ» без ведущего. */
export const ASSIGN_LEAD_ACTION = 'Сменить владельца…';
/** Протокол в режиме файла: пункт недоступен (FR-003). */
export const LEAD_FILE_REASON = 'Владельца меняют у протокола в системе';
/** Кнопка в строке ведущего окна «Доступ». */
export const CHANGE_LEAD_ACTION = 'Сменить…';
export const CHANGE_LEAD_LABEL = 'Сменить владельца';
export const NO_LEAD = 'Владельца нет';

export function assignLeadTitle(number: number): string {
  return `Сменить владельца П-${number}`;
}

export function currentLeadText(name: string): string {
  return `Сейчас ведёт: ${name}`;
}

// --- Шаг поиска ---

export const SEARCH_LABEL = 'Найти по ФИО или почте';
export const SEARCH_PLACEHOLDER = 'ФИО или почта';
export const TYPE_MORE = 'Введите ФИО или почту — от двух знаков';
export const SEARCHING = 'Поиск…';
export const LOOKUP_FAILED = 'Не удалось найти пользователей';
export const LEAD_STATE_FAILED = 'Не удалось узнать, кто ведёт протокол';
export const USERS_HEADING = 'Пользователи';
export const PEOPLE_HEADING = 'Из справочника — нет учётной записи';
export const LEADS_NOW = 'Ведёт сейчас';
export const EXTERNAL_USER_REASON =
  'Внешний пользователь — владельцем может быть только сотрудник компании';
export const OUTSIDE_DOMAINS = 'Почта вне доменов компании';

export function nobodyText(typed: string): string {
  return `Никого не нашлось по «${typed}»`;
}

export function foundText(count: number): string {
  return `Найдено: ${count}`;
}

/** «Создать пользователя «ряб»»; введена почта — «Создать пользователя ryabov@example.ru». */
export function createUserText(typed: string, email: boolean): string {
  return email ? `Создать пользователя ${typed}` : `Создать пользователя «${typed}»`;
}

/** Нет права «Пользователи: создать» (FR-017): кто может и что делать. */
export function adminsOnlyText(admins: readonly string[]): string {
  const who =
    admins.length > 0
      ? `Создать пользователя может администратор: ${admins.join(', ')}`
      : 'Создать пользователя может администратор';
  // ФИО «Бондарец Б. Б.» уже кончается точкой — второй не нужно.
  return `${sentenceEnd(who)} Попросите его создать учётную запись, затем назначьте владельца здесь`;
}

// --- Подтверждение смены и «Отменить» (FR-008 – FR-010) ---

export const CHANGE_LEAD_CONFIRM = 'Сменить владельца';

export function changeLeadTitle(number: number): string {
  return `Сменить владельца П-${number}?`;
}

/** «Сейчас ведёт Морозов О. С. После смены он сможет только читать протокол. Владельцем станет …». */
export function changeLeadText(
  current: string,
  after: LeadAfterAccess | null,
  next: string,
): string {
  const consequence = after ? ` После смены он ${leadAfterAccessText[after]}.` : '';
  return `${sentenceEnd(`Сейчас ведёт ${current}`)}${consequence} Владельцем станет ${next}`;
}

export function assignedText(number: number, name: string): string {
  return `Владелец П-${number}: ${name}`;
}

export function undoneText(number: number, name: string | null): string {
  return name ? `Владелец П-${number} — снова ${name}` : `У П-${number} снова нет владельца`;
}

// --- Шаг создания (FR-011 – FR-015, FR-018) ---

export const CREATE_SUBTITLE = 'Создаём учётную запись и назначаем владельцем протокола';
export const CREATE_AND_ASSIGN = 'Создать и назначить';
export const BACK = 'Назад';
export const OPEN_ACCESS = 'Открыть «Доступ»';
export const EXTERNAL_ACCESS_HINT =
  'Сотруднику другой компании протокол можно открыть в «Доступ» — редактором. Владельцем останется сотрудник';
export const ARCHIVED_TEXT =
  'Пользователь в архиве — вернуть его может администратор в «Пользователях»';
export const EXISTING_HINT = 'Его можно назначить владельцем без новой учётной записи';
/** REC-007 по personId: человека справочника нет или он не действует (дорожка A). */
export const PERSON_GONE = 'Вернитесь к поиску и найдите человека снова';
export const LINK_BY_EMAIL =
  'Если в справочнике есть человек с этой почтой, учётная запись свяжется с ним';

export function createTitle(number: number): string {
  return `Новый пользователь — владелец П-${number}`;
}

/**
 * Плашка роли: роль без выбора и письмо со ссылкой. 040: новый пользователь — сотрудник
 * и владелец этого протокола, роли «Администратор» у него не будет.
 */
export function roleText(number: number, title: string): string {
  return `Роль — Владелец П-${number} «${title}». Администратором не станет. Придёт письмо со ссылкой, чтобы задать пароль`;
}

export function linkPersonText(name: string): string {
  return `Учётная запись свяжется с человеком справочника ${name}`;
}

/** Есть прежний владелец: шаг создания показывает его и последствие (FR-014). */
export function leadNowText(name: string, after: LeadAfterAccess | null): string {
  return after
    ? `Сейчас ведёт ${name} — после смены он ${leadAfterAccessText[after]}`
    : `Сейчас ведёт ${name}`;
}

/** Почта вне корпоративных доменов (FR-018). */
export function domainOnlyText(domains: readonly string[]): string {
  return `Владельцем может быть только сотрудник компании: почта на домене ${domains.join(', ')}`;
}

export function existingText(name: string): string {
  return `Эта почта уже у пользователя ${name}`;
}

export function assignExistingLabel(name: string): string {
  return `Назначить ${name}`;
}

export function createdText(name: string, number: number, email: string): string {
  return `${name} — владелец П-${number}. Письмо отправлено на ${email}`;
}
